// src/components/digital_twin/twin_engine/ui/TwinHeader.js
// Drawer header: what the asset is (kind icon + register type), live / replay state, AI rating,
// and the headline numbers (ML health, failure probability, RUL, open alerts, ground elevation).
import React, { memo } from 'react';
import { Badge, Box, Flex, HStack, Icon, IconButton, SimpleGrid, Text, Tooltip } from '@chakra-ui/react';
import { FaBrain, FaHistory, FaSync, FaTimes } from 'react-icons/fa';
import { AI_GRADIENT, Stat, ago, useTwinTokens } from './ui';
import { TIER_META } from '../twinExplainer';

const healthColor = (s) => (s == null ? undefined : s < 40 ? 'red.400' : s < 60 ? 'orange.400' : s < 80 ? 'yellow.500' : 'green.400');

function TwinHeader({ asset, profile, brief, health, sources, sensorCount, isLive, historicalTimestamp, loading, lastPush, onRefresh, onClose }) {
  const t = useTwinTokens();
  const tier = TIER_META[brief.tier] || TIER_META.unknown;
  const alerts = sources.alerts?.data || [];
  const g = sources.elevation?.data?.ground_elevation_m ?? asset.ground_elevation_m;
  const well = sources.wellIntel?.data;
  return (
    <Box bg={t.card} borderBottomWidth="1px" borderColor={t.border} position="relative">
      <Box h="3px" bgGradient={AI_GRADIENT} />
      <Flex px={5} pt={4} pb={3} gap={3} align="flex-start">
        <Flex w="44px" h="44px" borderRadius="xl" align="center" justify="center" bg={`${profile.color}.500`} color="white" flexShrink={0} boxShadow="md">
          <Icon as={profile.icon} boxSize={5} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontSize="lg" fontWeight="900" color={t.text} noOfLines={1} title={asset.name}>{asset.name || `Asset ${asset.id}`}</Text>
          <HStack spacing={1.5} mt={1} flexWrap="wrap">
            <Badge colorScheme={profile.color} variant="solid" fontSize="10px" borderRadius="md">{profile.label}</Badge>
            {profile.typeLabel && profile.typeLabel.toLowerCase() !== profile.label.toLowerCase() && (
              <Badge variant="outline" fontSize="10px" borderRadius="md">{profile.typeLabel}</Badge>
            )}
            {isLive
              ? <Badge colorScheme="green" fontSize="10px" borderRadius="md">● LIVE</Badge>
              : <Badge colorScheme="purple" fontSize="10px" borderRadius="md"><Icon as={FaHistory} boxSize={2} mr={1} />REPLAY {historicalTimestamp ? new Date(historicalTimestamp).toLocaleString() : ''}</Badge>}
            <Tooltip label={brief.headline} hasArrow>
              <Badge colorScheme={tier.scheme} fontSize="10px" borderRadius="md"><Icon as={FaBrain} boxSize={2} mr={1} />AI · {tier.label}</Badge>
            </Tooltip>
            <Text fontSize="11px" color={t.faint}>ID {String(asset.id).slice(0, 12)}</Text>
          </HStack>
        </Box>
        <HStack spacing={1}>
          <Tooltip label={lastPush ? `Last live update ${ago(lastPush)}` : 'Reload every source'} hasArrow>
            <IconButton size="sm" variant="ghost" icon={<FaSync />} aria-label="Refresh asset data" isLoading={loading} onClick={onRefresh} />
          </Tooltip>
          {onClose && <IconButton size="sm" variant="ghost" icon={<FaTimes />} aria-label="Close digital twin" onClick={onClose} />}
        </HStack>
      </Flex>
      <SimpleGrid columns={{ base: 3, md: 5 }} spacing={3} px={5} pb={4}>
        <Stat label="ML health" value={health?.score != null ? Math.round(health.score) : '—'} sub={health?.status || (sources.health?.status === 'error' ? 'unavailable' : 'not scored')} color={healthColor(health?.score)} hint="Predictive-maintenance model score (0–100)" />
        {well?.intel?.score != null
          ? <Stat label="Well risk" value={well.intel.score} sub={well.intel.tier} color={TIER_META[well.intel.tier]?.hex} hint="Well intelligence risk score (higher = riskier)" />
          : <Stat label="Failure prob." value={health?.failureProbability != null ? `${Math.round(health.failureProbability * 100)}%` : '—'} color={health?.failureProbability > 0.5 ? 'red.400' : undefined} />}
        <Stat label="Remaining life" value={health?.rulDays != null ? `${Math.round(health.rulDays)} d` : '—'} />
        <Stat label="Open alerts" value={sources.alerts ? alerts.length : '—'} color={alerts.some((a) => a.severity === 'critical') ? 'red.400' : alerts.length ? 'orange.400' : undefined} sub={sensorCount ? `${sensorCount} sensor${sensorCount > 1 ? 's' : ''}` : 'no sensor'} />
        <Stat label="Ground elev." value={g != null ? `${Number(g).toFixed(1)} m` : '—'} sub={g != null ? 'TessaDEM' : 'not fetched'} />
      </SimpleGrid>
    </Box>
  );
}

export default memo(TwinHeader);
